import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Spinner, Lock, ShieldCheck } from '@phosphor-icons/react';
import { API } from '@/App';
import DropioLogo from '@/components/DropioLogo';

const POLL_INTERVAL = 2000;
const MAX_POLLS = 10;

const PaymentReturn = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');
  const cancelled = params.get('payment') === 'cancelled';

  const [status, setStatus] = useState(cancelled ? 'cancelled' : 'checking');
  const [details, setDetails] = useState(null);
  const pollsRef = useRef(0);

  useEffect(() => {
    if (cancelled || !sessionId) return;

    const poll = async () => {
      try {
        const res = await fetch(`${API}/payments/status/${sessionId}`, { credentials: 'include' });
        if (!res.ok) throw new Error('Status check failed');
        const data = await res.json();
        setDetails(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          return;
        }
        if (data.payment_status === 'expired' || pollsRef.current >= MAX_POLLS) {
          setStatus('timeout');
          return;
        }
        pollsRef.current += 1;
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        setStatus('error');
      }
    };
    poll();
  }, [sessionId, cancelled]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-10 max-w-md w-full text-center">
        <div className="flex justify-center mb-6"><DropioLogo size={40} /></div>

        {status === 'checking' && (
          <>
            <div className="w-20 h-20 rounded-full bg-purple-100 mx-auto flex items-center justify-center mb-5">
              <Spinner size={40} weight="bold" className="text-[#6D28D9] animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="pay-status-checking">Processing payment...</h2>
            <p className="text-gray-600 text-sm">Please wait while we confirm your transaction with Stripe.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 rounded-full bg-green-100 mx-auto flex items-center justify-center mb-5">
              <CheckCircle size={40} weight="fill" className="text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="pay-status-success">Payment successful!</h2>
            <p className="text-gray-600 text-sm mb-3">Your payment of <strong>${details?.amount?.toFixed(2)}</strong> is now held in escrow.</p>
            <div className="bg-purple-50 border border-purple-100 rounded-xl p-4 mb-5 text-left text-sm">
              <div className="flex items-start gap-2">
                <ShieldCheck size={18} weight="fill" className="text-[#6D28D9] flex-shrink-0 mt-0.5" />
                <p className="text-gray-700">Funds are protected and will be released to the traveler only after you confirm delivery.</p>
              </div>
            </div>
            <button onClick={() => navigate('/dashboard')} className="w-full dropio-gradient-cta text-white font-semibold py-3 rounded-lg" data-testid="pay-back-dashboard">
              Go to Dashboard
            </button>
          </>
        )}

        {(status === 'cancelled' || status === 'timeout' || status === 'error') && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-100 mx-auto flex items-center justify-center mb-5">
              <XCircle size={40} weight="fill" className="text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="pay-status-failed">
              {status === 'cancelled' ? 'Payment cancelled' : 'Something went wrong'}
            </h2>
            <p className="text-gray-600 text-sm mb-5">
              {status === 'cancelled' ? 'You cancelled the checkout. No charges were made.' : 'We couldn\'t confirm your payment. Please try again.'}
            </p>
            <button onClick={() => navigate('/dashboard')} className="w-full dropio-gradient-cta text-white font-semibold py-3 rounded-lg">
              Back to Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentReturn;
